import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Lottie from 'react-lottie';
import { ROUTES } from '../../routes/paths';
import { Features } from '../../../assets/features/Features';
import { sendAppointmentEmails } from '../../../data/email/appointmentEmailService';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';
import formDoneAnimation from '../../../assets/anim/form_done.json';
import gsapVideo from '../../../assets/anim/gsap.mp4';
import { useHeroVideoAnimation } from './useHeroVideoAnimation';
import { loadRecaptchaScript } from '../../../utils/loadRecaptcha';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const Hero: React.FC = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();

  const heroRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useHeroVideoAnimation(heroRef, videoRef);

  // Appointment quick booking state
  const [quickAppointment, setQuickAppointment] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Integrative Session',
    specialist: '',
    preferredDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [recaptchaError, setRecaptchaError] = useState('');
  // Privacy-policy acceptance — required to send, and gates loading of Google reCAPTCHA
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // reCAPTCHA widget state
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);
  // Counter that increments each time step 2 is entered — forces fresh remount of the widget div
  const [recaptchaMountKey, setRecaptchaMountKey] = useState(0);

  // Initialize reCAPTCHA widget when we enter step 2
  // (Google reCAPTCHA sets cookies — only loaded after the privacy checkbox is ticked.)
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || formStep !== 2 || showSuccess || !privacyAccepted) return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    const divEl = recaptchaRef.current;

    const init = async () => {
      await loadRecaptchaScript();
      if (cancelled) return;

      const tryRender = () => {
        if (!recaptchaRef.current || typeof (window as any).grecaptcha === 'undefined') return false;
        // Avoid double render in React Strict Mode
        if (recaptchaRef.current.hasAttribute('data-recaptcha-rendered')) return true;
        try {
          const id = (window as any).grecaptcha.render(recaptchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
            theme: 'light',
          });
          recaptchaRef.current.setAttribute('data-recaptcha-rendered', 'true');
          setRecaptchaWidgetId(id);
          return true;
        } catch (err) {
          console.error('reCAPTCHA render error:', err);
          return false;
        }
      };

      if (tryRender()) return;
      intervalId = setInterval(() => {
        if (tryRender() && intervalId) clearInterval(intervalId);
      }, 150);
    };

    const initTimeout = setTimeout(init, 100);
    return () => {
      cancelled = true;
      clearTimeout(initTimeout);
      if (intervalId) clearInterval(intervalId);
      // Clear the widget DOM contents to prevent lingering iframe on step change
      if (divEl) {
        divEl.innerHTML = '';
        divEl.removeAttribute('data-recaptcha-rendered');
      }
      setRecaptchaWidgetId(null);
    };
  }, [formStep, showSuccess, recaptchaMountKey, privacyAccepted]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setQuickAppointment(prev => ({ ...prev, [name]: value }));
  };

  const handleNextStep = (e: React.FormEvent) => {
    e.preventDefault();
    // Force a fresh reCAPTCHA widget each time step 2 is entered
    setRecaptchaMountKey((prev) => prev + 1);
    setRecaptchaWidgetId(null);
    setRecaptchaError('');
    setFormStep(2);
  };

  const handlePrevStep = () => {
    // Reset the widget before going back so no token lingers
    if ((window as any).grecaptcha && recaptchaWidgetId != null) {
      try {
        (window as any).grecaptcha.reset(recaptchaWidgetId);
      } catch (e) {
        // Ignore reset errors — the widget is about to unmount anyway
      }
    }
    setRecaptchaWidgetId(null);
    setRecaptchaError('');
    setFormStep(1);
  };

  const resetForm = () => {
    setQuickAppointment({
      name: '',
      email: '',
      phone: '',
      service: 'Integrative Session',
      specialist: '',
      preferredDate: ''
    });
    setShowSuccess(false);
    setFormStep(1);
    setRecaptchaError('');
    setRecaptchaWidgetId(null);
    setPrivacyAccepted(false);
  };

  const handleQuickBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecaptchaError('');

    if (!privacyAccepted) {
      setRecaptchaError(
        language === 'en'
          ? 'You must accept the Privacy Policy to send.'
          : 'Tem de aceitar a Política de Privacidade para enviar.'
      );
      return;
    }

    // Get reCAPTCHA token
    const token =
      typeof (window as any).grecaptcha !== 'undefined' && recaptchaWidgetId != null
        ? (window as any).grecaptcha.getResponse(recaptchaWidgetId)
        : '';

    if (!token) {
      setRecaptchaError(
        language === 'en'
          ? 'Please complete the reCAPTCHA verification.'
          : 'Por favor complete a verificação reCAPTCHA.'
      );
      return;
    }

    // Snapshot any state we still need after the optimistic switch
    const tokenSnapshot = token;
    const appointmentSnapshot = quickAppointment;

    setIsSubmitting(true);
    setShowSuccess(true);

    // Reset reCAPTCHA immediately so user can submit again later
    if ((window as any).grecaptcha && recaptchaWidgetId != null) {
      (window as any).grecaptcha.reset(recaptchaWidgetId);
    }

    // Fire-and-forget: don't block the UI on the email round-trip
    sendAppointmentEmails(
      {
        name: appointmentSnapshot.name,
        email: appointmentSnapshot.email,
        phone: appointmentSnapshot.phone,
        preferredDate: appointmentSnapshot.preferredDate,
        specialist: appointmentSnapshot.specialist,
      },
      tokenSnapshot
    ).catch((err) => console.error('Background mail send failed:', err));
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: formDoneAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  const formControlStyle = isDarkMode ? {
    backgroundColor: '#2a2c2e',
    color: '#ffffff',
    borderColor: '#3a3b3c'
  } : {};

  const formLabelStyle = isDarkMode ? {
    color: '#d1d5db'
  } : {};

  const cardStyle = isDarkMode ? {
    backgroundColor: '#242526',
    borderColor: '#3a3b3c'
  } : { backgroundColor: '#ffffff' };

  return (
    <section id="hero" ref={heroRef} className="hero section light-background position-relative">
      {/* Video background */}
      <div className="hero-video-container">
        <video
          ref={videoRef}
          src={gsapVideo}
          muted
          playsInline
          preload="auto"
          className="hero-video"
        />
        <div className="hero-vignette"></div>
      </div>

      <div className="container position-relative hero-content">
        <div className="row align-items-center min-vh-75">
          <div className="col-lg-6">
            <motion.div
              className="hero-text text-white"
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <h1 className="display-4 fw-bold mb-3 text-white">{Features.hero.title[language]}</h1>
              <h2 className="h3 mb-4 text-white">{Features.hero.subtitle[language]}</h2>
              <p className="lead mb-4 text-white">
                {Features.hero.description[language]}
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <button
                  onClick={() => {
                    if (window.location.pathname === '/') {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      navigate(ROUTES.HOME);
                    }
                  }}
                  className="btn btn-primary btn-lg px-4 rounded-pill"
                >
                  {Features.hero.bookSessionButton[language]}
                </button>
                <button
                  onClick={() => navigate(ROUTES.ABOUT_US)}
                  className="btn btn-outline-light btn-lg px-4 rounded-pill"
                >
                  {Features.hero.learnMoreButton[language]}
                </button>
              </div>
            </motion.div>
          </div>

          <div className="col-lg-5 offset-lg-1">
            <motion.div
              className="appointment-card shadow-lg rounded-4 p-4"
              style={cardStyle}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {!showSuccess ? (
                <>
                  <h3 className="text-primary text-center mb-4">{Features.hero.appointmentForm.title[language]}</h3>

                  <div className="progress mb-4" style={{ height: '8px' }}>
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{ width: formStep === 1 ? '50%' : '100%' }}
                      aria-valuenow={formStep === 1 ? 50 : 100}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="small" style={isDarkMode ? { color: '#d1d5db' } : { color: '#6c757d' }}>
                      {Features.hero.appointmentForm.personalInfo[language]}
                    </span>
                    <span className="small" style={isDarkMode ? { color: '#d1d5db' } : { color: '#6c757d' }}>
                      {Features.hero.appointmentForm.appointmentDetails[language]}
                    </span>
                  </div>

                  {formStep === 1 ? (
                    <form onSubmit={handleNextStep}>
                      <div className="mb-3">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            id="quick-name"
                            name="name"
                            placeholder={Features.hero.appointmentForm.nameLabel[language]}
                            value={quickAppointment.name}
                            onChange={handleInputChange}
                            style={formControlStyle}
                            required
                          />
                          <label htmlFor="quick-name" style={formLabelStyle}>
                            {Features.hero.appointmentForm.nameLabel[language]}
                          </label>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="form-floating">
                          <input
                            type="email"
                            className="form-control"
                            id="quick-email"
                            name="email"
                            placeholder={Features.hero.appointmentForm.emailLabel[language]}
                            value={quickAppointment.email}
                            onChange={handleInputChange}
                            style={formControlStyle}
                            required
                          />
                          <label htmlFor="quick-email" style={formLabelStyle}>
                            {Features.hero.appointmentForm.emailLabel[language]}
                          </label>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="form-floating">
                          <input
                            type="tel"
                            className="form-control"
                            id="quick-phone"
                            name="phone"
                            placeholder={Features.hero.appointmentForm.phoneLabel[language]}
                            value={quickAppointment.phone}
                            onChange={handleInputChange}
                            style={formControlStyle}
                            required
                          />
                          <label htmlFor="quick-phone" style={formLabelStyle}>
                            {Features.hero.appointmentForm.phoneLabel[language]}
                          </label>
                        </div>
                      </div>

                      <button type="submit" className="btn btn-primary w-100 py-3">
                        {Features.hero.appointmentForm.nextButton[language]}
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleQuickBooking}>
                      <div className="mb-3">
                        <div className="form-floating">
                          <input
                            type="date"
                            className="form-control"
                            id="quick-date"
                            name="preferredDate"
                            value={quickAppointment.preferredDate}
                            onChange={handleInputChange}
                            style={formControlStyle}
                            required
                          />
                          <label htmlFor="quick-date" style={formLabelStyle}>
                            {Features.hero.appointmentForm.preferredDateLabel[language]}
                          </label>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="form-floating">
                          <select
                            className="form-select"
                            id="quick-specialist"
                            name="specialist"
                            value={quickAppointment.specialist}
                            onChange={handleInputChange}
                            style={formControlStyle}
                            required
                          >
                            <option value="" disabled>{Features.hero.appointmentForm.selectSpecialist[language]}</option>
                            <option value="Dr. Guilherme Galvão">{Features.hero.appointmentForm.specialists.guilhermeGalvao[language]}</option>
                          </select>
                          <label htmlFor="quick-specialist" style={formLabelStyle}>
                            {Features.hero.appointmentForm.specialistLabel[language]}
                          </label>
                        </div>
                      </div>

                      {/* Privacy-policy acceptance — required, and loads the reCAPTCHA below */}
                      <div className="form-check text-start mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="quick-privacy-accept"
                          checked={privacyAccepted}
                          onChange={(e) => {
                            setPrivacyAccepted(e.target.checked);
                            setRecaptchaError('');
                          }}
                          required
                        />
                        <label className="form-check-label small" htmlFor="quick-privacy-accept">
                          {language === 'en' ? 'I accept the ' : 'Aceito a '}
                          <a
                            href={ROUTES.PRIVACY}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-underline text-primary"
                          >
                            {language === 'en' ? 'Privacy Policy' : 'Política de Privacidade'}
                          </a>
                          {language === 'en'
                            ? ' and consent to the processing of my personal data *'
                            : ' e concordo com o tratamento dos meus dados pessoais *'}
                        </label>
                      </div>

                      {/* reCAPTCHA widget — only shown on step 2 (appointment details) */}
                      <div className="mb-3 d-flex justify-content-center" role="group" aria-label="reCAPTCHA">
                        {!RECAPTCHA_SITE_KEY ? (
                          <p className="small text-muted mb-0">Configure REACT_APP_RECAPTCHA_SITE_KEY no .env</p>
                        ) : privacyAccepted ? (
                          <div key={`recaptcha-${recaptchaMountKey}`} ref={recaptchaRef} style={{ minHeight: 78 }} />
                        ) : (
                          <p className="small text-muted mb-0">
                            {language === 'en'
                              ? 'The anti-spam check (Google reCAPTCHA) loads once you tick the box above.'
                              : 'A verificação anti-spam (Google reCAPTCHA) é carregada ao marcar a caixa acima.'}
                          </p>
                        )}
                      </div>

                      {recaptchaError && (
                        <div className="alert alert-danger py-2 px-3 small mb-3">{recaptchaError}</div>
                      )}

                      <div className="d-flex gap-2 mt-4">
                        <button
                          type="button"
                          onClick={handlePrevStep}
                          className="btn btn-outline-secondary w-50 py-3"
                        >
                          {Features.hero.appointmentForm.backButton[language]}
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary w-50 py-3"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ?
                            Features.hero.appointmentForm.processingText[language] :
                            Features.hero.appointmentForm.scheduleNowButton[language]
                          }
                        </button>
                      </div>

                      <div className="text-center mt-3">
                        <small style={isDarkMode ? { color: '#d1d5db' } : { color: '#6c757d' }}>
                          {Features.hero.appointmentForm.contactConfirmation[language]}
                        </small>
                      </div>
                    </form>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="animation-container mb-3" style={{ height: '150px' }}>
                    <Lottie
                      options={defaultOptions}
                      height={150}
                      width={150}
                    />
                  </div>
                  <h3 className="text-success">{Features.hero.appointmentForm.successMessage.title[language]}</h3>
                  <p style={isDarkMode ? { color: '#f3f4f6' } : {}}>
                    {Features.hero.appointmentForm.successMessage.description[language]}
                  </p>

                  <button
                    className="btn btn-primary mt-3"
                    onClick={resetForm}
                  >
                    {Features.hero.appointmentForm.successMessage.makeAnotherAppointment[language]}
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Custom CSS for the hero section */}
      <style>{`
        .hero {
          position: relative;
          overflow: hidden;
          padding: 0;
          min-height: 100vh;
        }

        .hero-video-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .hero-video {
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          background: #000;
        }

        .hero-vignette {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 1;
          background: radial-gradient(
            ellipse at center,
            rgba(0, 0, 0, 0.15) 0%,
            rgba(0, 0, 0, 0.35) 50%,
            rgba(0, 0, 0, 0.65) 100%
          );
          pointer-events: none;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          padding: 70px 0;
        }

        .min-vh-75 {
          min-height: 75vh;
        }

        .appointment-card {
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: transform 0.3s ease;
        }

        .appointment-card:hover {
          transform: translateY(-5px);
        }

        .animation-container {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        /* Dark mode form styles */
        .dark-theme .form-control,
        .dark-theme .form-select {
          background-color: #2a2c2e !important;
          color: #ffffff !important;
          border-color: #3a3b3c !important;
        }

        .dark-theme .form-control::placeholder,
        .dark-theme .form-select::placeholder {
          color: #d1d5db !important;
        }

        .dark-theme .form-floating label {
          color: #d1d5db !important;
        }

        .dark-theme .appointment-card {
          background-color: #242526 !important;
          border-color: #3a3b3c !important;
        }

        .dark-theme .text-muted {
          color: #d1d5db !important;
        }

        /* Mobile responsive styles */
        @media (max-width: 991px) {
          .hero {
            min-height: auto;
          }

          .min-vh-75 {
            min-height: auto !important;
          }

          .hero-content {
            padding: 100px 0 40px;
          }

          .hero-text h1 {
            font-size: 2rem;
          }

          .hero-text h2 {
            font-size: 1.25rem;
          }

          .hero-text .lead {
            font-size: 0.95rem;
          }

          .hero-text .btn-lg {
            font-size: 0.9rem;
            padding: 0.5rem 1.25rem;
          }
        }

        @media (max-width: 767px) {
          .hero-text {
            padding-left: 20px;
            padding-right: 20px;
            margin-bottom: 30px;
          }

          .hero-content {
            padding: 90px 0 30px;
          }

          .hero-text h1 {
            font-size: 1.75rem;
          }

          .hero-text h2 {
            font-size: 1.1rem;
          }

          .appointment-card {
            padding: 15px !important;
            margin: 0 15px;
            max-width: calc(100% - 30px);
          }

          .form-floating > .form-control {
            padding: 0.75rem 0.75rem;
          }

          .form-floating > label {
            padding: 0.75rem 0.75rem;
          }

          .btn {
            padding: 0.5rem 1rem;
          }
        }

        @media (max-width: 480px) {
          .hero-content {
            padding: 80px 0 20px;
          }

          .hero-text h1 {
            font-size: 1.5rem;
            margin-bottom: 0.5rem !important;
          }

          .hero-text h2 {
            font-size: 1rem;
            margin-bottom: 0.75rem !important;
          }

          .hero-text .lead {
            font-size: 0.85rem;
            margin-bottom: 0.75rem !important;
          }

          .hero-text .btn-lg {
            font-size: 0.85rem;
            padding: 0.4rem 1rem;
          }

          .appointment-card {
            margin: 0 10px;
            max-width: calc(100% - 20px);
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;